/* eslint-disable quotes -- prettier governs quote style (double quotes avoid escaping apostrophes in alert copy) */
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import {
  carrierStore,
  customerStore,
  isCreditLimitExceeded,
  orderStore,
} from '@store/orders';
import { updateLoadPropertyAtIndex } from '@store/orders/actions/orderFormActions';
import {
  carrier,
  isCarrierDirty,
  isReefer,
  isTL,
  orderContainsLTLLoad,
} from '@store/orders/selectors';
import { Carrier, TrackingProviders } from '@store/orders/types';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { logError } from '@utils/errorLogging';
import { getEligibleEntries } from '@utils/utils';
import {
  customerStatusValidationErrorName,
  reeferValidationErrorName,
} from '@views/order/constants';
import { size } from 'lodash';
import { DateTime } from 'luxon';
import { createEffect } from 'solid-js';

import { HighwayItemsRow } from './HighwayItemsRow';
import { ReactivationRow } from './ReactivationRow';
import type { SystemAlert } from './types';

const BLOCKING_CUSTOMER_STATUSES = [
  'Deactivated',
  'Bankruptcy',
  'Credit Review',
  'Prepay',
  'RFP',
  'Legal Review',
];

const activeLoad = () => orderStore.order.loads[orderStore.activeTab.index];

const allMcsThatCanBypassReeferChecks = ['555609'];

const highwayReeferFailed = (c: Carrier | null): boolean => {
  if (allMcsThatCanBypassReeferChecks.includes(c?.mc ?? '')) return false; // Carrier ATG MC 555609 is exempt from highway reefer restriction
  if (!c?.id) return false;
  if (c.canBypassHwyReeferRestriction) return false;
  if (c.hasHwyReeferRestriction) return true;
  if (!c.hasReeferInsurance) return true;
  return false;
};

const hasHighwayIntel = (c: Carrier | null): boolean =>
  Boolean(c?.hasWatchDogAlerts) ||
  Boolean(c?.hasIdentityAlerts) ||
  Boolean(c?.carrierInsightViewModels?.length) ||
  Boolean(c?.failingHighwayRuleCodingsCount);

const insuranceMayNotCoverLoad = (c: Carrier | null): boolean => {
  if (!c?.id) return false;
  const load = activeLoad();
  if (['Billed', 'Paid', 'Closed'].includes(load.status ?? '')) return false;

  const cargoExpiration = DateTime.fromISO(c.cargoExpiration);
  const liabilityExpiration = DateTime.fromISO(c.liabilityExpiration);
  const stops = getEligibleEntries<LastUsedTopStopModel>(load.stops);
  if (size(stops) === 0) return false;

  const stopDates = stops.map((stop) =>
    DateTime.fromISO(stop.stopDateTime!).plus({ days: 7 }),
  );
  const latestStop = DateTime.max(...stopDates);
  if (!latestStop) return false;

  return (
    (!c.hasExpiredCargoInsurance && cargoExpiration <= latestStop) ||
    (!c.hasExpiredLiabilityInsurance && liabilityExpiration <= latestStop)
  );
};

const trackingProvider = (): string | undefined => {
  for (const key in TrackingProviders) {
    if (
      TrackingProviders[key as keyof typeof TrackingProviders] ===
      orderStore.order.requiredTrackingService
    ) {
      return key;
    }
  }
  return undefined;
};

export const createCarrierAlerts = (): (() => SystemAlert[]) => {
  // Save-gating effects must run even when not blocking, to reset the flags.
  const reeferBlocks = () =>
    isReefer() &&
    isTL() &&
    highwayReeferFailed(carrier()) &&
    isCarrierDirty() &&
    isFeatureFlagEnabled(
      FeatureFlags.RestrictRefrigeratedLoadsByHWYAssessment,
    )();

  createEffect(() => {
    const shouldForce = reeferBlocks();
    updateLoadPropertyAtIndex({ forceCarrierChange: shouldForce });
    if (shouldForce) {
      logError(
        reeferValidationErrorName,
        new Error(reeferValidationErrorName),
        {
          orderId: orderStore.order.id,
        },
      );
    }
  });

  const customerStatusBlocks = () => {
    if (
      !isFeatureFlagEnabled(
        FeatureFlags.BlockActionsForBlockingCustomerStatuses,
      )()
    ) {
      return false;
    }
    return BLOCKING_CUSTOMER_STATUSES.includes(customerStore.customer.status);
  };

  createEffect(() => {
    const shouldForce = customerStatusBlocks();
    updateLoadPropertyAtIndex({ forceCustomerChange: shouldForce });
    if (shouldForce) {
      logError(
        customerStatusValidationErrorName,
        new Error(customerStatusValidationErrorName),
        { orderId: orderStore.order.id },
      );
    }
  });

  const buildBlockAlerts = (): SystemAlert[] => {
    const c = carrier();
    const load = activeLoad();
    const alerts: SystemAlert[] = [];

    // 1. Negative credit limit (customer selected on order)
    if (
      isFeatureFlagEnabled(
        FeatureFlags.feature_order_block_save_on_neg_credit_limit,
      )() &&
      isCreditLimitExceeded() &&
      !orderContainsLTLLoad() &&
      orderStore.isCreate
    ) {
      alerts.push({
        key: 'negative-credit-limit',
        severity: 'block',
        priority: 1,
        title: 'Carrier Assignment Blocked',
        message:
          'Carrier cannot be assigned to load until additional credit has been approved by the Credit Team.',
      });
    }

    // 1. Blocking customer status
    if (customerStatusBlocks()) {
      alerts.push({
        key: 'customer-status',
        severity: 'block',
        priority: 1,
        message: `A carrier cannot be assigned to this load due to the current customer status of ${customerStore.customer.status}.`,
      });
    }

    // 2. Reactivation required (block) / requested (notice, once submitted)
    if (
      ['Pending', 'Conditional', 'Inactive'].includes(
        load.carrierStatus ?? '',
      ) &&
      Boolean(load.carrierId) &&
      isFeatureFlagEnabled(FeatureFlags.CarrierActivationManagement)()
    ) {
      const reactivationPending = Boolean(
        (carrierStore.carrier as Array<Carrier | undefined>).find(
          (c) => c?.id === load.carrierId,
        )?.carrierReactivationRequestCacheItemViewModel,
      );
      alerts.push({
        key: 'reactivation-required',
        severity: reactivationPending ? 'notice' : 'block',
        priority: reactivationPending ? 0 : 2,
        render: () => <ReactivationRow />,
      });
    }
    //NOTE: Restricted carrier alert is currently commented out. Please dont remove it.
    // alerts.push({
    //   key: 'restricted-carrier',
    //   severity: 'block',
    //   priority: 2,
    //   render: () => (
    //     <RestrictedCarrierRow
    //       matchedOn="MC 123456"
    //       message="This customer has requested that this carrier not be used."
    //       reason="CUSTOMER_REQUEST"
    //       onOverride={() => openModal(OVERRIDE_RESTRICTED_CARRIER_MODAL_ID)}
    //     />
    //   ),
    // });

    if (!c?.id) return alerts;

    // 3. Carrier isn't active
    if (!c.isActive && c.status !== 'Conditional' && c.contacts.length !== 0) {
      alerts.push({
        key: 'carrier-not-active',
        severity: 'block',
        priority: 3,
        message: "Carrier isn't active.",
      });
    }

    // 4. Conditional safety rating
    if (c.status === 'Conditional') {
      alerts.push({
        key: 'conditional-safety-rating',
        severity: 'block',
        priority: 4,
        message: c.carrierActionPlan
          ? 'This carrier has a conditional safety rating.'
          : 'This carrier has a conditional safety rating and must be reviewed before booking, please contact the carrier team: carriers@armstrongtransport.com.',
      });
    }

    // 5. Highway refrigerated freight requirements
    if (reeferBlocks()) {
      alerts.push({
        key: 'highway-reefer',
        severity: 'block',
        priority: 5,
        message:
          'Carrier does not meet Highway Refrigerated Freight requirements and is not eligible to move this load. To proceed with booking, please select a different carrier. If you believe this carrier does have proper coverage, please reach out to compliance.',
      });
    }

    // 6. Expired cargo and/or liability insurance
    if (c.hasExpiredCargoInsurance || c.hasExpiredLiabilityInsurance) {
      alerts.push({
        key: 'expired-insurance',
        severity: 'block',
        priority: 6,
        message: 'Carrier has expired cargo and/or liability insurance.',
      });
    }

    return alerts;
  };

  const buildWarningAlerts = (): SystemAlert[] => {
    const c = carrier();
    const load = activeLoad();
    const alerts: SystemAlert[] = [];

    // 7. Insurance dates may not cover the full load (warning, per Jira AC)
    if (insuranceMayNotCoverLoad(c)) {
      alerts.push({
        key: 'insurance-coverage',
        severity: 'warning',
        priority: 7,
        message:
          'Please ensure insurance dates cover the full load; if not, contact the carrier team for an updated certificate.',
      });
    }

    // 8. LTL load, carrier not LTL enabled
    if (c?.id && !c.ltlEnabled && load.mode === 'LTL') {
      alerts.push({
        key: 'ltl-not-enabled',
        severity: 'warning',
        priority: 8,
        message: 'This is an LTL load and the carrier is not LTL enabled.',
      });
    }

    // 9. Customer requires tracking
    if (Boolean(orderStore.order.trackLoads)) {
      const provider = trackingProvider();
      alerts.push({
        key: 'requires-tracking',
        severity: 'warning',
        priority: 9,
        message:
          provider == null
            ? 'This customer requires tracking on their loads.'
            : `This customer prefers ${provider} for tracking.`,
      });
    }

    if (!c?.id) return alerts;

    // 10. Carrier can't receive fuel advances
    if (!c.advancesAllowed) {
      alerts.push({
        key: 'no-fuel-advances',
        severity: 'warning',
        priority: 10,
        message: "This carrier can't receive fuel advances.",
      });
    }

    // 11. No contacts
    if (
      c.contacts.length === 0 &&
      !isFeatureFlagEnabled(FeatureFlags.perm_disable_hwy_carrier_validations)()
    ) {
      alerts.push({
        key: 'no-contacts',
        severity: 'warning',
        priority: 11,
        message:
          "This carrier has no contacts. Rate confirmations can't be generated until you add a contact.",
      });
    }

    // 12. Highway items to review
    if (
      Boolean(c.highwayUrl) &&
      hasHighwayIntel(c) &&
      !isFeatureFlagEnabled(FeatureFlags.perm_disable_hwy_carrier_validations)()
    ) {
      alerts.push({
        key: 'highway-items',
        severity: 'warning',
        priority: 12,
        render: () => <HighwayItemsRow />,
      });
    }

    // 13. Blacklisted for your office
    if (c.blacklisted) {
      alerts.push({
        key: 'blacklisted',
        severity: 'warning',
        priority: 13,
        message: 'This carrier is blacklisted for your office.',
      });
    }
    //NOTE: Restricted carrier alert is currently commented out. Please dont remove it.
    // alerts.push({
    //   key: 'flagged-carrier',
    //   severity: 'warning',
    //   priority: 14,
    //   render: () => <FlaggedCarrierRow
    //     matchedOn="MC 123456 — matched a duplicate carrier record"
    //     message="This carrier has a documented service failure with this customer."
    //     reason="SERVICE_FAILURE"
    //     onAcknowledge={() => openModal(ACKNOWLEDGE_CARRIER_RESTRICTION_MODAL_ID)}
    //     />
    // });

    // Extras (not in the ticket priority table — kept, sorted last)
    if (c.brokerAuthority) {
      alerts.push({
        key: 'broker-authority',
        severity: 'warning',
        priority: 90,
        message: 'Use caution - this carrier has broker authority.',
      });
    }

    if (c.firstLoad) {
      alerts.push({
        key: 'first-load',
        severity: 'warning',
        priority: 91,
        message:
          "This is Armstrong's first load with this Carrier. Take extra care before issuing advances of any kind.",
      });
    }

    return alerts;
  };

  return (): SystemAlert[] => [...buildBlockAlerts(), ...buildWarningAlerts()];
};
